import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { FaPencil, FaQuestion } from 'react-icons/fa6';
import { FaThumbsDown, FaThumbsUp, FaClock } from 'react-icons/fa';
import { EventParticipationAnswer } from '../../../types/event';
import { useLocale } from '../../../hooks/useLanguage';
import { formatEventTime, formatFriendlyEventDate } from '../../../utils/eventUtils';
import IconBadge from '../../icons/IconBadge';
import Icon from '../../icons/Icon';
import { COLOR_ALERT, COLOR_ERROR, COLOR_SUCCESS } from '../../../constants';

type Props = {
  eventId: number;
  dateOption: any;
  dateOptionAnswer?: { answer?: EventParticipationAnswer; description?: string };
  onSave: (eventId: number, dateOptionId: number, answer: EventParticipationAnswer, description?: string) => void;
};

function EventSelectDateOption({ eventId, dateOption, dateOptionAnswer, onSave = () => {} }: Props) {
  const locale = useLocale();
  const { t } = useTranslation();

  const [showDescription, setShowDescription] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<EventParticipationAnswer>(
    dateOptionAnswer?.answer || EventParticipationAnswer.PENDING,
  );
  const [descriptionValue, setDescriptionValue] = useState<string>(dateOptionAnswer?.description || '');
  const [clickedButton, setClickedButton] = useState<EventParticipationAnswer | null>(null);

  useEffect(() => {
    if (clickedButton && clickedButton !== selectedAnswer) {
      const timer = setTimeout(() => {
        setClickedButton(null);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [clickedButton, selectedAnswer]);

  useEffect(() => {
    if (selectedAnswer === EventParticipationAnswer.PENDING) return;
    onSave(eventId, dateOption.id, selectedAnswer, descriptionValue.trim() || '');
    if (descriptionValue) setShowDescription(true);
  }, [selectedAnswer, descriptionValue]);

  const handleButtonClick = (answer: EventParticipationAnswer) => {
    setSelectedAnswer(answer);
    setClickedButton(answer);
  };

  const getButtonText = (answer: EventParticipationAnswer) => {
    switch (answer) {
      case EventParticipationAnswer.YES:
        return t('page.events.infoScreen.attendance.yes');
      case EventParticipationAnswer.MAYBE:
        return t('page.events.infoScreen.attendance.maybe');
      case EventParticipationAnswer.NO:
        return t('page.events.infoScreen.attendance.no');
      default:
        return '';
    }
  };

  const shouldShowPill = (answer: EventParticipationAnswer) => {
    return selectedAnswer === answer || clickedButton === answer;
  };
  const formattedDate = formatFriendlyEventDate(
    new Date(dateOption.startTime),
    dateOption.endTime ? new Date(dateOption.endTime) : undefined,
    locale,
  );
  const formattedTime = formatEventTime(dateOption.startTime, dateOption.endTime);

  return (
    <div className="flex flex-col gap-2 border-b-2 border-secondary-50 pb-2">
      <div className="text-lg text-primary-900">{formattedDate}</div>
      <div className="flex justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconBadge size="small" icon={FaClock} className="h-8 w-8 rounded-lg bg-secondary p-2 text-primary" />
          <div className="ftext-sm text-primary-900">{formattedTime}</div>
        </div>
        <div className="flex items-center justify-center gap-2">
          {/* YES Button */}
          <div className="relative flex items-center">
            <div
              className={`flex items-center transition-all duration-300 ${
                shouldShowPill(EventParticipationAnswer.YES) ? 'opacity-100' : 'absolute opacity-0'
              }`}>
              <button
                type="button"
                onClick={() => handleButtonClick(EventParticipationAnswer.YES)}
                className={`flex h-12 w-12 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 ${
                  selectedAnswer === EventParticipationAnswer.YES ? 'shadow-lg' : 'hover:scale-105'
                } ${shouldShowPill(EventParticipationAnswer.YES) ? 'rounded-r-none' : 'rounded-full'}`}
                style={{ backgroundColor: COLOR_SUCCESS }}>
                <Icon icon={FaThumbsUp} />
              </button>
              <div
                className={`flex h-12 items-center rounded-r-full px-3 text-sm font-medium text-white transition-all duration-300 ${
                  shouldShowPill(EventParticipationAnswer.YES)
                    ? 'w-auto translate-x-0 opacity-70'
                    : 'w-0 -translate-x-4 overflow-hidden opacity-0'
                }`}
                style={{ backgroundColor: COLOR_SUCCESS }}>
                {getButtonText(EventParticipationAnswer.YES)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleButtonClick(EventParticipationAnswer.YES)}
              className={`flex h-12 w-12 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 ${
                selectedAnswer === EventParticipationAnswer.YES ? 'shadow-lg' : 'hover:scale-105'
              } ${shouldShowPill(EventParticipationAnswer.YES) ? 'absolute opacity-0' : 'opacity-100'}`}
              style={{ backgroundColor: COLOR_SUCCESS }}>
              <Icon icon={FaThumbsUp} />
            </button>
          </div>

          {/* MAYBE Button */}
          <div className="relative flex items-center">
            <div
              className={`flex items-center transition-all duration-300 ${
                shouldShowPill(EventParticipationAnswer.MAYBE) ? 'opacity-100' : 'absolute opacity-0'
              }`}>
              <button
                type="button"
                onClick={() => handleButtonClick(EventParticipationAnswer.MAYBE)}
                className={`flex h-12 w-12 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 ${
                  selectedAnswer === EventParticipationAnswer.MAYBE ? 'shadow-lg' : 'hover:scale-105'
                } ${shouldShowPill(EventParticipationAnswer.MAYBE) ? 'rounded-r-none' : 'rounded-full'}`}
                style={{ backgroundColor: COLOR_ALERT }}>
                <Icon icon={FaQuestion} />
              </button>
              <div
                className={`flex h-12 items-center rounded-r-full px-3 text-sm font-medium text-white transition-all duration-300 ${
                  shouldShowPill(EventParticipationAnswer.MAYBE)
                    ? 'w-auto translate-x-0 opacity-70'
                    : 'w-0 -translate-x-4 overflow-hidden opacity-0'
                }`}
                style={{ backgroundColor: COLOR_ALERT }}>
                {getButtonText(EventParticipationAnswer.MAYBE)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleButtonClick(EventParticipationAnswer.MAYBE)}
              className={`flex h-12 w-12 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 ${
                selectedAnswer === EventParticipationAnswer.MAYBE ? 'shadow-lg' : 'hover:scale-105'
              } ${shouldShowPill(EventParticipationAnswer.MAYBE) ? 'absolute opacity-0' : 'opacity-100'}`}
              style={{ backgroundColor: COLOR_ALERT }}>
              <Icon icon={FaQuestion} />
            </button>
          </div>

          {/* NO Button */}
          <div className="relative flex items-center">
            <div
              className={`flex items-center transition-all duration-300 ${
                shouldShowPill(EventParticipationAnswer.NO) ? 'opacity-100' : 'absolute opacity-0'
              }`}>
              <button
                type="button"
                onClick={() => handleButtonClick(EventParticipationAnswer.NO)}
                className={`flex h-12 w-12 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 ${
                  selectedAnswer === EventParticipationAnswer.NO ? 'shadow-lg' : 'hover:scale-105'
                } ${shouldShowPill(EventParticipationAnswer.NO) ? 'rounded-r-none' : 'rounded-full'}`}
                style={{ backgroundColor: COLOR_ERROR }}>
                <Icon icon={FaThumbsDown} />
              </button>
              <div
                className={`flex h-12 items-center rounded-r-full px-3 text-sm font-medium text-white transition-all duration-300 ${
                  shouldShowPill(EventParticipationAnswer.NO)
                    ? 'w-auto translate-x-0 opacity-70'
                    : 'w-0 -translate-x-4 overflow-hidden opacity-0'
                }`}
                style={{ backgroundColor: COLOR_ERROR }}>
                {getButtonText(EventParticipationAnswer.NO)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleButtonClick(EventParticipationAnswer.NO)}
              className={`flex h-12 w-12 items-center justify-center rounded-full text-secondary-50 transition-all duration-200 ${
                selectedAnswer === EventParticipationAnswer.NO ? 'shadow-lg' : 'hover:scale-105'
              } ${shouldShowPill(EventParticipationAnswer.NO) ? 'absolute opacity-0' : 'opacity-100'}`}
              style={{ backgroundColor: COLOR_ERROR }}>
              <Icon icon={FaThumbsDown} />
            </button>
          </div>
        </div>
      </div>
      {showDescription ? (
        <input
          type="text"
          value={descriptionValue}
          onChange={(e) => setDescriptionValue(e.target.value)}
          placeholder={`${t('page.events.invite.note')}...`}
          className="text-sm"
          autoFocus
        />
      ) : (
        <button
          type="button"
          className="flex shrink items-center gap-2 self-end font-bold text-primary hover:underline"
          onClick={() => setShowDescription(true)}>
          <Icon icon={FaPencil} className="text-primary" />
          {t('page.events.invite.addNote')}
        </button>
      )}
    </div>
  );
}

export default EventSelectDateOption;
